import { Injectable } from "@angular/core";
import { filter } from "rxjs/operators";
import * as ld from "launchdarkly-js-client-sdk";
import { LDClient, LDFlagValue, LDFlagChangeset, LDContext } from "launchdarkly-js-client-sdk";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
    ld = ld;
    private ldClient: LDClient | undefined;
    private flags: { [key: string]: BehaviorSubject<LDFlagValue> } = {};
    private initPromise: Promise<void> | null = null;

    /**
     * Initializes the LaunchDarkly client. Called by APP_INITIALIZER.
     * Safe to call multiple times — subsequent calls await the original initialization.
     * @param context The user context for LaunchDarkly. Defaults to an anonymous user if not provided.
     */
    async initialize(context?: LDContext): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }
        
        const userContext = context ?? {
            kind: 'user',
            anonymous: true,
        };

        const clientSideId = environment.launchDarkly.clientSideId;
        this.ldClient = this.ld.initialize(clientSideId, userContext);

        this.initPromise = (async () => {
            try {
                await this.ldClient!.waitForInitialization();
                this.registerForFlagChanges();
            } catch (error) {
                console.error('Error initializing LaunchDarkly client: ', error);
            }
        })();

        return this.initPromise;
    }

    /**
     * Gets an observable for a launchdarkly feature flag.
     * @param flagName The name of the feature flag.
     * @param defaultValue The default value to return if the flag is not found. Defaults to false if not provided.
     * @returns An observable that emits the current value of the launchdarkly feature flag.
     */
    getFlagValue(flagName: string, defaultValue: LDFlagValue = false): Observable<LDFlagValue> {
        if (!this.flags[flagName]) {
            const initialValue = this.ldClient?.variation(flagName, defaultValue) ?? defaultValue;
            this.flags[flagName] = new BehaviorSubject<LDFlagValue>(initialValue);
        }    
        return this.flags[flagName].asObservable()
            .pipe(filter(value => value !== undefined));
    }

    /**
     * Current flag value from the LD client (synchronous). Use when building UI synchronously (e.g. global nav).
     * Before initialization, returns defaultValue.
     */
    getFlagValueSync(flagName: string, defaultValue: LDFlagValue = false): LDFlagValue {
        return this.ldClient?.variation(flagName, defaultValue) ?? defaultValue;
    }

    /**
     * Closes the LaunchDarkly client connection.
     */
    close(): void {
        if (this.ldClient) {
            this.ldClient.close();
            this.ldClient = undefined;
            this.initPromise = null;
            this.flags = {};
            console.log('LaunchDarkly client closed.');
        }
    }

    private registerForFlagChanges() {
        if (!this.ldClient) { return; }

        this.ldClient.on('change', (changes: LDFlagChangeset) => {
            console.log('Received flag changes from LaunchDarkly:', changes);
            // Push each changed flag's BehaviorSubject with the new value
            Object.entries(changes).forEach(([flagName, change]) => {
                const flagSubject = this.flags[flagName];
                if (flagSubject) {
                    flagSubject.next(change.current);
                }
            });        
        });  
    }    
}
